import React, {useEffect, useRef, useState} from "react";
import {
  Activity,
  Ban,
  BatteryMedium,
  Bike,
  Bluetooth,
  Building2,
  BusFront,
  CalendarDays,
  CarFront,
  Check,
  ChevronRight,
  Clock,
  Copy,
  Cpu,
  Gauge,
  Hash,
  Lock,
  LockOpen,
  Map as MapIcon,
  MapPin,
  Navigation,
  Power,
  RotateCcw,
  Route,
  Satellite,
  Signal,
  Smartphone,
  Tag,
  Trash2,
  Truck,
  UserRound,
  X,
} from "lucide-react";
import {getGeolocationFromLatLng} from "../../utils/utility";
import {isFt920} from "../../utils/deviceStatus";
import {TYPE_LABELS, canOpenLiveMap, canOpenRouteHistory, deviceDetails, deviceVitals, timeAgo} from "./consoleModel";
import {OnlinePill, Pill} from "./ConsoleParts";

const VEHICLE_ICONS = {TRUCK: Truck, VAN: Truck, CAR: CarFront, BIKE: Bike, BUS: BusFront};

function typeIcon(device) {
  if (!device) return Navigation;
  if (device.assetType === "GT06_TRACKER") return VEHICLE_ICONS[String(device.vehicleType || "").toUpperCase()] || Navigation;
  return {GPS_LOCK: Lock, BT_LOCK: Bluetooth, MOBI_TRACK: Smartphone}[device.assetType] || Navigation;
}

const VITAL_ICONS = {speed: Gauge, ignition: Power, fix: Satellite, battery: BatteryMedium, contact: Signal, report: Bluetooth, duty: Activity, position: MapPin};
const DETAIL_ICONS = {person: UserRound, unitId: Hash, vehicle: CarFront, protocol: Cpu, status: Tag, registered: CalendarDays};
const TONE_TEXT = {on: "text-success-700", warn: "text-warning-700", off: "text-danger-700", none: "text-ink-900"};

// border-solid: the app's button reset (styles/base/reset.css) sets border: none on every button.
const BTN = "inline-flex items-center gap-1.5 h-9 px-3 text-sm font-semibold rounded-lg border border-solid transition-colors";

/** Address for a position, looked up once per position. */
function useAddress(lat, lng) {
  const [address, setAddress] = useState(null);
  useEffect(() => {
    let alive = true;
    setAddress(null);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return undefined;
    getGeolocationFromLatLng(lat, lng)
      .then((a) => alive && setAddress(typeof a === "string" && a !== "Invalid input" ? a : ""))
      .catch(() => alive && setAddress(""));
    return () => {
      alive = false;
    };
  }, [lat, lng]);
  return address;
}

/** Small copy-to-clipboard button; hidden where the browser has no clipboard access. */
function CopyButton({text, label}) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(t);
  }, [copied]);
  if (!text || typeof navigator === "undefined" || !navigator.clipboard) return null;
  return (
    <button
      type="button"
      onClick={() => navigator.clipboard.writeText(text).then(() => setCopied(true), () => {})}
      aria-label={copied ? `${label} copied` : `Copy ${label}`}
      title={copied ? "Copied" : `Copy ${label}`}
      className="inline-grid place-items-center h-6 w-6 shrink-0 rounded-md text-ink-400 hover:text-ink-700 hover:bg-ink-100"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-success-600" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

function VitalTile({vital}) {
  const Icon = vital.key === "lock" ? (vital.value === "Open" ? LockOpen : Lock) : VITAL_ICONS[vital.key] || Activity;
  const empty = vital.value === "—";
  return (
    <div className="min-w-0 rounded-xl border border-ink-200 bg-white px-3 py-2.5">
      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink-500">
        <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span className="truncate">{vital.label}</span>
      </p>
      <p className={`mt-1 text-xl font-semibold leading-tight tabular-nums ${empty ? "text-ink-400" : TONE_TEXT[vital.tone] || TONE_TEXT.none}`}>
        {vital.value}
        {vital.unit && <span className="ml-1 text-xs font-medium text-ink-500">{vital.unit}</span>}
      </p>
      {vital.note && <p className="mt-0.5 text-[11px] leading-snug text-ink-500">{vital.note}</p>}
    </div>
  );
}

function DetailRow({row}) {
  const Icon = DETAIL_ICONS[row.key] || Tag;
  return (
    <div className="flex items-start justify-between gap-4 px-3 py-2.5">
      <dt className="flex items-center gap-2 text-sm text-ink-500 shrink-0">
        <Icon className="h-4 w-4 text-ink-400" aria-hidden="true" />
        {row.label}
      </dt>
      <dd className="m-0 min-w-0 text-right">
        <span className="inline-flex items-center justify-end gap-1.5 max-w-full">
          {row.key === "person" && !row.muted && (
            <span className="h-6 w-6 shrink-0 rounded-full bg-brand-50 text-brand-700 text-xs font-bold grid place-items-center" aria-hidden="true">
              {row.value.charAt(0).toUpperCase()}
            </span>
          )}
          <span
            className={`break-all text-sm ${row.muted ? "text-ink-400" : "font-semibold text-ink-900"} ${row.mono ? "font-mono text-[13px] font-medium" : ""}`}
          >
            {row.value}
          </span>
          {row.copy && <CopyButton text={row.value} label={row.label} />}
        </span>
        {row.note && <small className="block text-xs text-ink-400">{row.note}</small>}
      </dd>
    </div>
  );
}

function LoadingBody() {
  return (
    <div className="flex flex-col gap-3 animate-pulse" aria-hidden="true">
      <div className="grid grid-cols-3 gap-2">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-[72px] rounded-xl bg-ink-100" />
        ))}
      </div>
      <div className="h-24 rounded-xl bg-ink-100" />
      <div className="h-14 rounded-xl bg-ink-100" />
      <div className="h-48 rounded-xl bg-ink-100" />
    </div>
  );
}

/**
 * Slide-in panel for one device. device is null while loading; error is a message to show.
 * The live map and route history reuse the dashboard's own modals (opened by the parent).
 */
export default function DevicePanel({device, error, onClose, onOpenClient, onLiveMap, onRouteHistory, onToggleSuspend, onDelete}) {
  const closeRef = useRef(null);
  const address = useAddress(device?.latitude, device?.longitude);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const hasPosition = Number.isFinite(device?.latitude) && Number.isFinite(device?.longitude);
  const suspended = device?.assetStatus === "SUSPENDED";
  const TypeIcon = typeIcon(device);
  const vitals = device ? deviceVitals(device) : [];
  const typeLine = device
    ? `${TYPE_LABELS[device.assetType] || device.assetType}${device.assetType === "GT06_TRACKER" ? ` · ${isFt920(device) ? "FT920" : "GT06"}` : ""}`
    : "Device";
  const coords = hasPosition ? `${device.latitude.toFixed(5)}, ${device.longitude.toFixed(5)}` : "";

  return (
    <>
      <div className="sac-scrim" onClick={onClose} aria-hidden="true" />
      <aside className="sac-drawer" role="dialog" aria-modal="true" aria-label="Device details">
        <header className="shrink-0 px-5 pt-5 pb-4 border-b border-ink-100 bg-gradient-to-b from-brand-50 to-white">
          <div className="flex items-start gap-3">
            <span className="h-11 w-11 shrink-0 rounded-xl bg-white border border-brand-100 shadow-xs grid place-items-center text-brand-700">
              <TypeIcon className="h-5 w-5" aria-hidden="true" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-brand-600">{typeLine}</p>
              <h2 className="text-lg font-semibold leading-snug text-ink-900 break-words">{device ? device.name : error ? "Device" : "Loading…"}</h2>
            </div>
            <button
              ref={closeRef}
              type="button"
              onClick={onClose}
              aria-label="Close device details"
              className="h-9 w-9 shrink-0 grid place-items-center rounded-lg text-ink-500 hover:bg-white hover:text-ink-800 border border-solid border-transparent hover:border-ink-200"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
          {device && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <OnlinePill device={device} />
              {suspended && <Pill tone="warn">Suspended</Pill>}
              <span className="inline-flex items-center gap-1 text-xs text-ink-500">
                <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                {!device.lastContactUtc
                  ? "No contact yet"
                  : `${device.assetType === "BT_LOCK" ? "Last report" : "Last contact"} ${timeAgo(device.lastContactUtc)}`}
              </span>
            </div>
          )}
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 flex flex-col gap-4">
          {error && <p className="rounded-lg border border-danger-100 bg-danger-50 px-3 py-2.5 text-sm text-danger-700">{error}</p>}
          {!device && !error && <LoadingBody />}

          {device && (
            <>
              {vitals.length > 0 && (
                <div className={`grid gap-2 ${vitals.length === 3 ? "grid-cols-3" : "grid-cols-2"}`}>
                  {vitals.map((v) => (
                    <VitalTile key={v.key} vital={v} />
                  ))}
                </div>
              )}

              <section className="rounded-xl border border-ink-200 bg-white px-3 py-3 flex gap-3">
                <span className="h-9 w-9 shrink-0 rounded-full bg-accent-50 text-accent-600 grid place-items-center">
                  <MapPin className="h-4 w-4" aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-500">Last known position</p>
                  {hasPosition ? (
                    <>
                      <p className="mt-0.5 text-sm leading-snug text-ink-900">
                        {address == null ? "Looking up the address…" : address || "Address unavailable"}
                      </p>
                      <p className="mt-1 flex items-center gap-1 font-mono text-xs text-ink-500">
                        {coords}
                        <CopyButton text={coords} label="coordinates" />
                      </p>
                    </>
                  ) : (
                    <p className="mt-0.5 text-sm text-ink-500">No position reported yet</p>
                  )}
                </div>
              </section>

              <button
                type="button"
                onClick={() => onOpenClient(device.clientId)}
                className="group flex items-center gap-3 w-full rounded-xl border border-solid border-ink-200 bg-white px-3 py-2.5 text-left hover:border-brand-300 hover:bg-brand-50/40"
              >
                <span className="h-9 w-9 shrink-0 rounded-lg bg-brand-50 text-brand-700 grid place-items-center">
                  <Building2 className="h-4 w-4" aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[11px] font-semibold uppercase tracking-wider text-ink-500">Client</span>
                  <span className="block truncate font-semibold text-ink-900">{device.clientName}</span>
                </span>
                <span className="inline-flex items-center text-xs font-medium text-ink-500 group-hover:text-brand-700">
                  Open
                  <ChevronRight className="h-4 w-4" aria-hidden="true" />
                </span>
              </button>

              <section>
                <h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink-500">Details</h3>
                <dl className="m-0 rounded-xl border border-ink-200 bg-white divide-y divide-ink-100">
                  {deviceDetails(device).map((row) => (
                    <DetailRow key={row.key} row={row} />
                  ))}
                </dl>
              </section>

              <p className="text-xs text-ink-400">Online status comes from the server, with the same rules as the dashboards.</p>
            </>
          )}
        </div>

        {device && (
          <footer className="shrink-0 px-5 py-3 border-t border-ink-100 bg-white flex flex-wrap items-center gap-2">
            {canOpenLiveMap(device) && (
              <button type="button" onClick={onLiveMap} className={`${BTN} bg-brand-700 border-brand-700 text-white hover:bg-brand-800`}>
                <MapIcon className="h-4 w-4" aria-hidden="true" />
                Open live map
              </button>
            )}
            {canOpenRouteHistory(device) && (
              <button type="button" onClick={onRouteHistory} className={`${BTN} bg-white border-ink-300 text-ink-800 hover:bg-ink-50`}>
                <Route className="h-4 w-4" aria-hidden="true" />
                Route history
              </button>
            )}
            <span className="flex-1" />
            <button
              type="button"
              onClick={onToggleSuspend}
              className={`${BTN} border-transparent ${suspended ? "text-success-700 hover:bg-success-50" : "text-danger-700 hover:bg-danger-50"}`}
            >
              {suspended ? <RotateCcw className="h-4 w-4" aria-hidden="true" /> : <Ban className="h-4 w-4" aria-hidden="true" />}
              {suspended ? "Activate" : "Suspend"}
            </button>
            <button
              type="button"
              onClick={onDelete}
              className={`${BTN} border-transparent text-danger-700 hover:bg-danger-50`}
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
              Delete
            </button>
          </footer>
        )}
      </aside>
    </>
  );
}
